import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import '../assets/Sidecart.css';
import '../assets/Sidecart-variation.css';
import BlockHeader from './SC_Header/BlockHeader.tsx';
import BlockHeaderV2 from './SC_Header/HeaderVariation/BlockHeaderV2.tsx';
import BlockContent from './SC_Content/BlockContent.tsx';
import BlockFooter from './SC_Footer/BlockFooter.tsx';
import BlockFooterV2 from './SC_Footer/FooterVariation/BlockFooterV2.tsx';
import BlockFooterV3 from './SC_Footer/FooterVariation/BlockFooterV3.tsx';
import SC_ManageData from './SC_ManageData';
import { 
  handleAddToCartShop, 
  handleAddToCartSimple, 
  handleAddToCartBundle, 
  handleAddToCartVariation, 
  handleAddToCartEventTicket, 
  handleAddToCartYITHTogether 
} from './SC_AddToCartHandler.tsx';
import { RootState } from '../../redux/checkoutredux/store/checkoutStore';

interface ModulSidecartProps {
  sidebarOpen: boolean;
  toggleSidebar: () => void;
}

const ModulSidecart: React.FC<ModulSidecartProps> = ({ sidebarOpen, toggleSidebar }) => {
  // Redux-Store-Zugriff
  const store = useSelector((state: RootState) => state);
  const products = useSelector((state: RootState) => state.shoppingcart.productdata);
  const metacartdata = useSelector((state: RootState) => state.shoppingcart.metacartdata);
  const sidecartSettings = useSelector((state: RootState) => state.sidecartsettings.appSettings);

  // Lokaler State
  const sidecartRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [spinnerloading, setSpinnerLoading] = useState(false);
  const [currentProductId, setCurrentProductId] = useState<string[]>([]);
  const [currentCartKeyId, setCurrentCartKeyId] = useState<string[]>([]);
  const [networkRequestOccurred, setNetworkRequestOccurred] = useState(false);

  // --- Stable Handler-Referenzen via useCallback ---
  const handleAddToCartButton = useCallback((event: Event) => {
    handleAddToCartSimple(
      (event as unknown) as React.MouseEvent<HTMLButtonElement>,
      toggleSidebar,
      setCurrentProductId,
      sidecartSettings,
      products,
      setLoading,
      setSpinnerLoading
    );
  }, [toggleSidebar, sidecartSettings, products]);

  const handleAddToCartButtonSub = useCallback((event: Event) => {
    handleAddToCartSimple(
      (event as unknown) as React.MouseEvent<HTMLButtonElement>,
      toggleSidebar,
      setCurrentProductId,
      sidecartSettings,
      products,
      setLoading,
      setSpinnerLoading
    );
  }, [toggleSidebar, sidecartSettings, products]);

  const handleAddToCartBundleHandler = useCallback((event: Event) => {
    handleAddToCartBundle(
      (event as unknown) as React.MouseEvent<HTMLButtonElement>,
      toggleSidebar,
      setCurrentProductId,
      sidecartSettings,
      products,
      setLoading,
      setSpinnerLoading
    );
  }, [toggleSidebar, sidecartSettings, products]);

  const handleAddToCartVariationHandler = useCallback((event: Event) => {
    handleAddToCartVariation(
      (event as unknown) as React.MouseEvent<HTMLButtonElement>,
      toggleSidebar,
      setCurrentProductId,
      sidecartSettings,
      products,
      setLoading,
      setSpinnerLoading
    );
  }, [toggleSidebar, sidecartSettings, products]);

  const handleAddToCartTicketEventHandler = useCallback((event: Event) => {
    handleAddToCartEventTicket(
      (event as unknown) as React.MouseEvent<HTMLButtonElement>,
      toggleSidebar,
      setCurrentProductId,
      sidecartSettings,
      products,
      setLoading,
      setSpinnerLoading
    );
  }, [toggleSidebar, sidecartSettings, products]);

  const handleAddToCartShopButtons = useCallback((event: Event) => {
    handleAddToCartShop(
      (event as unknown) as React.MouseEvent<HTMLButtonElement>,
      toggleSidebar,
      setCurrentProductId,
      sidecartSettings,
      products,
      setLoading,
      setSpinnerLoading
    );
  }, [toggleSidebar, sidecartSettings, products]);

  const handleAddToCartYithTogetherHandler = useCallback((event: Event) => {
    handleAddToCartYITHTogether(
      (event as unknown) as React.MouseEvent<HTMLButtonElement>,
      toggleSidebar,
      setCurrentProductId,
      sidecartSettings,
      products,
      setLoading,
      setSpinnerLoading
    );
  }, [toggleSidebar, sidecartSettings, products]);

  // --- Ende Handler-Definition ---

  // Main Counter aktualisieren
  useEffect(() => {
    if (metacartdata && metacartdata.length > 0) {
      const quantity = metacartdata[0].quantityall;
      const totalformatprice = metacartdata[0].totalpriceformat;
      SC_ManageData.manageMainCounter(quantity, totalformatprice, sidecartSettings.cartslider);
    } else {
      SC_ManageData.manageMainCounter(0, 0, sidecartSettings.cartslider);
    }
  }, [metacartdata, sidecartSettings]);

  // Header- und Footer-Komponenten basierend auf Settings
  const HeaderComponents = {
    '/headerelements/headerview2': BlockHeaderV2,
    default: BlockHeader,
  };
  const FooterComponents = {
    '/footerelements/footerelement2': BlockFooterV2,
    '/footerelements/footerelement3': BlockFooterV3,
    default: BlockFooter,
  };
  const HeaderComponent = HeaderComponents[sidecartSettings.headervariation] || HeaderComponents.default;
  const FooterComponent = FooterComponents[sidecartSettings.footervariation] || FooterComponents.default;

  // --- Click-Outside Handler ---
  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (sidebarOpen && sidecartRef.current && !sidecartRef.current.contains(event.target as Node)) {
      toggleSidebar();
    }
  }, [sidebarOpen, toggleSidebar]);

  // --- Global Listener Setup ---
  const setupGlobalListeners = useCallback(() => {
    const originalXHROpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method: string, url: string) {
      this.addEventListener('load', () => {
        setNetworkRequestOccurred(true);
      });
      return originalXHROpen.apply(this, arguments as unknown as [string, string]);
    };

    const originalFetch = window.fetch;
    window.fetch = async (url, options) => {
      const response = await originalFetch(url, options);
      setNetworkRequestOccurred(true);
      return response;
    };
  }, []);

  // --- Add-to-Cart Listener Setup ---
  const setupAddToCartListener = useCallback(() => {
    const addToCartButton = document.querySelector<HTMLButtonElement>(
      '.product.type-product.product-type-simple .single_add_to_cart_button'
    );
    const addToCartButtonSub = document.querySelector<HTMLButtonElement>(
      '.product.type-product.product-type-subscription .single_add_to_cart_button'
    );
    const addToCartBundle = document.querySelector<HTMLButtonElement>(
      '.product.type-product.product-type-bundle .single_add_to_cart_button'
    );
    const addToCartVariation = document.querySelector<HTMLButtonElement>(
      '.product.type-product.product-type-variable .single_add_to_cart_button'
    );
    const addToCartTicketEvent = document.querySelector<HTMLButtonElement>(
      '.product.type-product.product-type-ticket-event .single_add_to_cart_button'
    );
    const addToCartYithTogether = document.querySelector<HTMLButtonElement>(
      '.product.type-product .yith-wfbt-submit-button'
    );
    const addToCartButtons = document.querySelectorAll<HTMLAnchorElement>(
      'a.add_to_cart_button:not(.product_type_variable):not(.product_type_ticket-event)'
    );
    const ipsWooScConts = document.querySelectorAll<HTMLDivElement>('.ips-woo-sc-cont');

    if (addToCartButton) addToCartButton.addEventListener('click', handleAddToCartButton);
    if (addToCartButtonSub) addToCartButtonSub.addEventListener('click', handleAddToCartButtonSub);
    if (addToCartBundle) addToCartBundle.addEventListener('click', handleAddToCartBundleHandler);
    if (addToCartVariation) addToCartVariation.addEventListener('click', handleAddToCartVariationHandler);
    if (addToCartTicketEvent) addToCartTicketEvent.addEventListener('click', handleAddToCartTicketEventHandler);
    if (addToCartYithTogether) addToCartYithTogether.addEventListener('click', handleAddToCartYithTogetherHandler);

    addToCartButtons.forEach((button) =>
      button.addEventListener('click', handleAddToCartShopButtons)
    );

    ipsWooScConts.forEach((ipsWooScCont) => {
      ipsWooScCont.addEventListener('click', (event) => {
        event.preventDefault();
        toggleSidebar();
      });
    });
  }, [
    handleAddToCartButton,
    handleAddToCartButtonSub,
    handleAddToCartBundleHandler,
    handleAddToCartVariationHandler,
    handleAddToCartTicketEventHandler,
    handleAddToCartShopButtons,
    handleAddToCartYithTogetherHandler,
    toggleSidebar,
  ]);

  // --- Remove Add-to-Cart Listener ---
  const removeAddToCartListener = useCallback(() => {
    const addToCartButton = document.querySelector<HTMLButtonElement>(
      '.product.type-product.product-type-simple .single_add_to_cart_button'
    );
    const addToCartButtonSub = document.querySelector<HTMLButtonElement>(
      '.product.type-product.product-type-subscription .single_add_to_cart_button'
    );
    const addToCartBundle = document.querySelector<HTMLButtonElement>(
      '.product.type-product.product-type-bundle .single_add_to_cart_button'
    );
    const addToCartVariation = document.querySelector<HTMLButtonElement>(
      '.product.type-product.product-type-variable .single_add_to_cart_button'
    );
    const addToCartTicketEvent = document.querySelector<HTMLButtonElement>(
      '.product.type-product.product-type-ticket-event .single_add_to_cart_button'
    );
    const addToCartYithTogether = document.querySelector<HTMLButtonElement>(
      '.product.type-product .yith-wfbt-submit-button'
    );
    const addToCartButtons = document.querySelectorAll<HTMLAnchorElement>(
      '.add_to_cart_button'
    );
    const ipsWooScConts = document.querySelectorAll<HTMLDivElement>('.ips-woo-sc-cont');

    if (addToCartButton) addToCartButton.removeEventListener('click', handleAddToCartButton);
    if (addToCartButtonSub) addToCartButtonSub.removeEventListener('click', handleAddToCartButtonSub);
    if (addToCartBundle) addToCartBundle.removeEventListener('click', handleAddToCartBundleHandler);
    if (addToCartVariation) addToCartVariation.removeEventListener('click', handleAddToCartVariationHandler);
    if (addToCartTicketEvent) addToCartTicketEvent.removeEventListener('click', handleAddToCartTicketEventHandler);
    if (addToCartYithTogether) addToCartYithTogether.removeEventListener('click', handleAddToCartYithTogetherHandler);
    addToCartButtons.forEach((button) =>
      button.removeEventListener('click', handleAddToCartShopButtons)
    );
    ipsWooScConts.forEach((ipsWooScCont) => {
      ipsWooScCont.removeEventListener('click', toggleSidebar);
    });
  }, [
    handleAddToCartButton,
    handleAddToCartButtonSub,
    handleAddToCartBundleHandler,
    handleAddToCartVariationHandler,
    handleAddToCartTicketEventHandler,
    handleAddToCartShopButtons,
    handleAddToCartYithTogetherHandler,
    toggleSidebar,
  ]);

  // --- Setup und Cleanup für "mousedown" (Click-Outside) ---
  useEffect(() => {
    if (sidecartSettings.cartslider) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [sidebarOpen, handleClickOutside, sidecartSettings.cartslider]);

  // --- Setup und Cleanup der Add-to-Cart Listener und globalen Listener ---
  useEffect(() => {
    setupAddToCartListener();
    if (!networkRequestOccurred) {
      window.addEventListener('load', setupGlobalListeners);
    }
    if (networkRequestOccurred) {
      setNetworkRequestOccurred(false);
    }
    return () => {
      removeAddToCartListener();
      window.removeEventListener('load', setupGlobalListeners);
    };
  }, [networkRequestOccurred, setupAddToCartListener, removeAddToCartListener, setupGlobalListeners]);

  return (
    <>
      {sidecartSettings.cartslider && (
        <>
          <span className={`ips-sidecartback-opac ${sidebarOpen ? 'active' : ''}`}></span>
          <div
            ref={sidecartRef}
            id="ips-sidecart"
            className={`bootstrap-ips ips-sidecart-container-right ${sidebarOpen ? 'active' : ''}`}
            style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}
          >
            <div className="row g-0 ipssidecartheader flex-grow-0">
              <HeaderComponent toggleSidebar={toggleSidebar} />
            </div>
            <div className="row ipssidecartcontent flex-grow-1">
              <BlockContent
                loading={loading}
                spinnerloading={spinnerloading}
                currentProductId={currentProductId}
                setCurrentProductId={setCurrentProductId}
                currentCartKeyId={currentCartKeyId}
                setCurrentCartKeyId={setCurrentCartKeyId}
                setLoading={setLoading}
              />
            </div>
            <div className="row g-0 ipssidecartfooter flex-grow-0">
              <FooterComponent />
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default ModulSidecart;